import {
  ArrowBack,
  AssignmentTurnedIn,
  EditNoteOutlined,
  Logout,
  Message,
  Notifications,
  Person,
  Phone,
} from "@mui/icons-material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLogoutHandler } from "src/authentication/data-access/useLoginQuery";
import { useAuth } from "../context/useAuth";
import i18n from "../services/languages/i18n";
import CountDownSessionTimer from "../ui/CountDownSessionsTimer";
import {
  Badge,
  Button,
  CircularProgress,
  Divider,
  keyframes,
  List,
  ListItem,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import { useGlobalMessage } from "../context/globalMessage";
import useDisableImpersonation from "src/admin/data-access/ImpersonationPage/useDisableImpersonation";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useDrawerOptions } from "./Sider/Drawer/useDrawerOptions";
import { useUnreadEmailCount } from "src/email/mailbox/data-access/useGetMailBoxEmailsUnreadCount";
import MenuBookIcon from "@mui/icons-material/MenuBook";

export default function PrimarySearchAppBar() {
  const { t } = useTranslation();
  const {
    userInfo,
    authenticated,
    isChamber,
    isNonGemiUser,
    userHomePath,
    isBusiness,
    sessionTimedOut,
    isCompanyPerson,
    userCantAccess,
    setUserCantAccess,
  } = useAuth();
  const { mutate: logout } = useLogoutHandler();

  const sideBarOptions = useDrawerOptions();

  const langOptions = [
    { value: "en", label: "English" },
    { value: "el", label: "Ελληνικά" },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [langugageAnchorEl, setLanguageAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const [language, setLanguage] = React.useState(
    langOptions.find((lang) => lang.value === localStorage.getItem("lng"))
      ?.label,
  );

  const isMenuOpen = Boolean(anchorEl);

  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const isNavigationMenuOpen = Boolean(menuAnchor);

  const { maintenance, infoMessage, chamberMessage } = useGlobalMessage();

  const [anchorElNotification, setAnchorElNotification] =
    React.useState<null | HTMLElement>(null);
  const notificationOpen = Boolean(anchorElNotification);

  const [anchorElDocs, setAnchorElDocs] = React.useState<null | HTMLElement>(
    null,
  );
  const isDocsMenuOpen = Boolean(anchorElDocs);

  const handleDocsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElDocs(event.currentTarget);
  };

  const handleDocsMenuClose = () => {
    setAnchorElDocs(null);
  };

  const docsMenuOptions =
    !authenticated || isBusiness
      ? [
          ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
            ? [
                {
                  label: t("Εγχειρίδιο χρήσης υποβολής καταγγελιών"),
                  level: 1,
                  link: "/manuals/complaintSubmissionManual.pdf",
                  isExternal: true,
                },
              ]
            : []),
          ...(import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
            ? [
                {
                  label: t("Εγχειρίδιο χρήσης υπόθεσης Παράβασης-Προστίμου"),
                  level: 1,
                  link: "/manuals/violationCaseManualForObligor.pdf",
                  isExternal: true,
                },
              ]
            : []),
          {
            label: t("Εγχειρίδιο χρήσης προσωποποιημένων υπηρεσιών Γ.Ε.ΜΗ."),
            level: 1,
            link: "/manuals/manualBusiness.pdf",
            isExternal: true,
          },
        ]
      : isNonGemiUser
        ? [
            ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
              ? [
                  {
                    label: t("Εγχειρίδιο χρήσης υποβολής καταγγελιών"),
                    level: 1,
                    link: "/manuals/complaintSubmissionManual.pdf",
                    isExternal: true,
                  },
                ]
              : []),
          ]
        : [];

  const handleClickNotification = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNotification(event.currentTarget);
  };
  const handleCloseNotification = () => {
    setAnchorElNotification(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = (e: any) => {
    setLanguageAnchorEl(null);
    const selectedValue = e?.target?.attributes?.value?.value;
    if (!selectedValue) return;
    i18n.changeLanguage(selectedValue);
    localStorage.setItem("lng", selectedValue);
    if (!e.target.innerText) return;
    setLanguage(e.target.innerText);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleLogoutClick = async () => {
    handleMenuClose();
    logout();
  };
  const { data: myEmailsUnreadCount, isLoading: isLoadingUnread } =
    useUnreadEmailCount(isBusiness, isNonGemiUser);

  React.useEffect(() => {
    if (sessionTimedOut) {
      navigate("/");
    }
  }, [sessionTimedOut]);

  React.useEffect(() => {
    if (userCantAccess) {
      navigate(userHomePath);
      setUserCantAccess(false);
    }
  }, [userCantAccess]);

  const { mutate: disableImpersonation, isLoading: isImpersonationLoading } =
    useDisableImpersonation();

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      {(isBusiness || isNonGemiUser) && [
        <Box>
          <Link
            to="/email/mailbox"
            style={{ textDecoration: "none", color: "black" }}
          >
            <MenuItem
              sx={{
                textDecoration: "none",
                color: "black",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: ".5rem",
              }}
            >
              <Message style={{ width: "1rem", height: "1rem" }} />
              <p>{t("Θυρίδα Μηνυμάτων")}</p>
            </MenuItem>
          </Link>
          <Link
            to={isNonGemiUser ? "/nonGemi/changeInfo" : "/business/changeInfo"}
            style={{ textDecoration: "none", color: "black" }}
          >
            <MenuItem
              sx={{
                textDecoration: "none",
                color: "black",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: ".5rem",
              }}
            >
              <Person style={{ width: "1rem", height: "1rem" }} />
              <p>{t("Στοιχεία χρήστη και πρόσβασης")}</p>
            </MenuItem>
          </Link>
        </Box>,
      ]}
      {isBusiness && (
        <Link
          to="/business/changeCompanyInfo"
          style={{ textDecoration: "none", color: "black" }}
        >
          <MenuItem
            sx={{
              textDecoration: "none",
              color: "black",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: ".5rem",
            }}
          >
            <Phone style={{ width: "1rem", height: "1rem" }} />
            <p>{t("Στοιχεία επικοινωνίας")}</p>
          </MenuItem>
        </Link>
      )}

      {import.meta.env.VITE_EMPA_ACTIVATED === "true" && isBusiness && (
        <Link
          to="/empa/form"
          style={{ textDecoration: "none", color: "black" }}
        >
          <MenuItem
            sx={{
              textDecoration: "none",
              color: "black",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: ".5rem",
            }}
          >
            <EditNoteOutlined style={{ width: "1rem", height: "1rem" }} />
            <p>{t("Ερωτηματολόγιο ΕΜΠΑ")}</p>
          </MenuItem>
        </Link>
      )}

      {import.meta.env.VITE_FIREBASE_ACTIVE === "true" &&
      (isBusiness || isNonGemiUser) ? (
        <Link
          to="/changeNotifications"
          style={{ textDecoration: "none", color: "black" }}
        >
          <MenuItem
            sx={{
              textDecoration: "none",
              color: "black",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: ".5rem",
            }}
          >
            <Notifications style={{ width: "1rem", height: "1rem" }} />
            <p>{t("Αλλαγή ρυθμίσεων ειδοποιήσεων")}</p>
          </MenuItem>
        </Link>
      ) : null}

      {userInfo?.isImpersonation && (
        <MenuItem
          sx={{
            textDecoration: "none",
            color: "black",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: ".5rem",
          }}
          onClick={() => {
            disableImpersonation();
          }}
          disabled={isImpersonationLoading}
        >
          <ArrowBack style={{ width: "1rem", height: "1rem" }} />
          {isImpersonationLoading ? (
            <CircularProgress />
          ) : (
            <p>
              {userInfo?.isBusiness
                ? t("Επιστροφή σε Chamber")
                : t("Επιστροφή σε Admin")}
            </p>
          )}
        </MenuItem>
      )}
      <MenuItem
        sx={{
          textDecoration: "none",
          color: "black",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: ".5rem",
        }}
        onClick={handleLogoutClick}
      >
        <Logout style={{ width: "1rem", height: "1rem" }} />
        <p>{t("Αποσύνδεση")}</p>
      </MenuItem>
    </Menu>
  );

  const renderLanguageMenu = (
    <Menu
      anchorEl={langugageAnchorEl}
      open={Boolean(langugageAnchorEl)}
      onClose={handleLanguageMenuClose}
    >
      {langOptions.map((lang) => (
        <MenuItem
          sx={{ py: 2, px: 3 }}
          key={lang.value}
          value={lang.value}
          onClick={handleLanguageMenuClose}
        >
          {lang.label}
        </MenuItem>
      ))}
    </Menu>
  );

  const ringAnimation = keyframes`
		0% {
			transform: rotate(0);
		}
		10% {
			transform: rotate(15deg);
		}
		20% {
			transform: rotate(-10deg);
		}
		30% {
          transform: rotate(10deg);
      }
      40% {
          transform: rotate(-5deg);
      }
      50% {
		  transform: rotate(5deg);
	  }
		60% {
			transform: rotate(0);
		}
		100% {
			transform: rotate(0);
		}
	`;

  const renderSystemNotification = (
    <Box>
      <Tooltip title={t("Ειδοποιήσεις Συστήματος")}>
        <IconButton
          onClick={handleClickNotification}
          sx={{
            animation:
              maintenance ||
              infoMessage.message ||
              (chamberMessage.message && isChamber)
                ? `${ringAnimation} 1s ease-in-out`
                : "",
          }}
        >
          <Badge
            //@ts-ignore
            color={
              maintenance
                ? "error"
                : infoMessage.message || (chamberMessage.message && isChamber)
                  ? "info"
                  : "transparent"
            }
            badgeContent={
              maintenance ? (
                <>!</>
              ) : infoMessage.message ||
                (chamberMessage.message && isChamber) ? (
                <>i</>
              ) : (
                <></>
              )
            }
          >
            <NotificationsRoundedIcon sx={{ color: "white" }} />
          </Badge>
        </IconButton>
      </Tooltip>
    </Box>
  );
  const renderMailboxNotification = (
    <Box>
      <Tooltip title={t("Θυρίδα Μηνυμάτων")}>
        <Link
          to="/email/mailbox"
          style={{ textDecoration: "none", color: "black" }}
        >
          <IconButton
            sx={{
              animation: isLoadingUnread
                ? `${ringAnimation} 1s ease-in-out`
                : "",
            }}
          >
            <Badge
              color="error"
              overlap="circular"
              badgeContent={
                isLoadingUnread ? (
                  <CircularProgress size={10} color="inherit" />
                ) : myEmailsUnreadCount?.data?.myEmailsUnreadCount > 0 ? (
                  myEmailsUnreadCount?.data?.myEmailsUnreadCount
                ) : null
              }
            >
              <Message sx={{ color: "white" }} />
            </Badge>
          </IconButton>
        </Link>
      </Tooltip>
    </Box>
  );

  const renderNavigationMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={isNavigationMenuOpen}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      MenuListProps={{
        sx: { p: 0, width: { md: "70vw", sm: "100vw" } },
      }}
      onClose={() => setMenuAnchor(null)}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            background: "white",
            top: 0,
            zIndex: 1000,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              px: 1,
              fontSize: { xs: ".9rem", md: "1.5rem" },
              width: "fit-content",
              fontWeight: "bold",
              color: "#676767",
            }}
          >
            {t("Μεταβείτε στην Υπηρεσία που Επιθυμείτε")}
          </Typography>
          <IconButton
            sx={{ width: "2rem", alignSelf: "end" }}
            onClick={() => setMenuAnchor(null)}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            width: { lg: "70vw", md: "90vw" },
            minHeight: "30vh",
            flexDirection: "column",
            backgroundColor: "white",
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              width: "100%",
              flexWrap: "wrap",
              p: 1,
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Button
              sx={{
                display: "flex",
                height: "2.5rem",
                border: "2px solid #e7e7ea",
                background: "white",
                color: "#676767",
                gap: "0.2rem",
                px: 15,
                "&:hover": {
                  backgroundColor: "#f4f4f5",
                },
              }}
              onClick={() => {
                navigate(userHomePath);
                setMenuAnchor(null);
              }}
            >
              <HomeOutlinedIcon sx={{ color: "#676767" }} />
              {t("Αρχική")}
            </Button>
            <Link
              to="/publicCertificateDownload/form"
              onClick={() => setMenuAnchor(null)}
              style={{ textDecoration: "none" }}
            >
              <Button
                variant="contained"
                sx={{
                  display: "flex",
                  height: "2.5rem",
                  textWrap: "wrap",
                  flexDirection: "row",
                  gap: "1rem",
                  textDecoration: "none",
                }}
              >
                <AssignmentTurnedIn />
                <Box>{t("Έλεγχος Γνησιότητας")}</Box>
              </Button>
            </Link>
          </Box>
          <Divider variant="fullWidth" flexItem orientation="horizontal" />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(17rem, 20rem))",
              textWrap: "wrap",
              gap: 3,
              p: 2,
              width: "100%",
            }}
          >
            {sideBarOptions.map((option, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {option.image}
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      width: "20rem",
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
                <List key={index}>
                  {option?.children?.map((child, idx) => (
                    <ListItem key={idx}>
                      <Link
                        onClick={() => {
                          const page = window.location.pathname.split("/")[2];
                          localStorage.removeItem(`searchFilters-${page}`);
                          if (child.isExternal) {
                            window.open(
                              `${window.location.origin}${child.link}`,
                            );
                          }
                          setMenuAnchor(null);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "blue"; // Underline on hover
                        }}
                        onMouseLeave={(e) => {
                          if (location.pathname != child.link) {
                            e.currentTarget.style.color = "black";
                          }
                        }}
                        color="inherit"
                        style={{
                          color:
                            location.pathname == child.link ? "blue" : "black",
                          textDecoration: "none",
                        }}
                        to={child.isExternal ? location.pathname : child.link}
                      >
                        {location.pathname == child.link && "→"} {child.label}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Menu>
  );

  const renderNotificationMenu = (
    <Menu
      anchorEl={anchorElNotification}
      open={
        notificationOpen &&
        ((infoMessage.message ||
          maintenance ||
          (isChamber && chamberMessage.message)) as boolean)
      }
      onClose={handleCloseNotification}
      onClick={handleCloseNotification}
      keepMounted
      MenuListProps={{
        sx: { p: 0 },
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          overflowWrap: "break-word",
          maxWidth: "25rem",
          flexDirection: "column",
          gap: 2,
        }}
      ></Box>
      {maintenance && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            overflowWrap: "break-word",
            maxWidth: "25rem",
            background: "#FFCCCC",
            cursor: "pointer",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {t("Έχουν προγραμματιστεί εργασίες συντήρησης στο σύστημα για τις")}{" "}
            {maintenance}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {t(
              "Για λίγα λεπτά το σύστημα θα είναι μη διαθέσιμο. Παρακαλούμε αποφύγετε τη χρήση του συστήματος λίγο πριν την προαναφερόμενη ώρα. Σας ευχαριστούμε για την κατανόηση.",
            )}
          </Typography>
        </Box>
      )}
      {infoMessage.message && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            overflowWrap: "break-word",
            maxWidth: "25rem",
            background: "#D9EDF7",
            cursor: "pointer",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            <strong>{t("Ενημέρωση")}:</strong> &nbsp;
            <span dangerouslySetInnerHTML={{ __html: infoMessage.message }} />
          </Typography>
        </Box>
      )}
      {chamberMessage.message && isChamber && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            overflowWrap: "break-word",
            maxWidth: "25rem",
            background: "#FED7A0",
            cursor: "pointer",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            <strong>{t("Ενημέρωση ΥΓΕΜΗ")}:</strong> &nbsp;{" "}
            <span
              dangerouslySetInnerHTML={{ __html: chamberMessage.message }}
            />
          </Typography>
        </Box>
      )}
    </Menu>
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        backgroundColor: "#003476",
        width: "100%",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          height: "80%",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Link
            style={{ textDecoration: "none", color: "white" }}
            to={userHomePath}
          >
            <Typography variant="h6" noWrap>
              {isSmallScreen ? t("Γ.Ε.ΜΗ.") : t("ΓΕΝΙΚΟ ΕΜΠΟΡΙΚΟ ΜΗΤΡΩΟ")}
            </Typography>
          </Link>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            gap: { xs: 0.1, md: 1 },
          }}
        >
          {authenticated && (
            <>
              <Button
                sx={{
                  display: "flex",
                  color: "white",
                  gap: "0.2rem",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  setMenuAnchor(event.currentTarget)
                }
              >
                <KeyboardArrowDownOutlinedIcon
                  sx={{
                    transform: menuAnchor ? "rotate(180deg)" : "",
                    transition: ".2s ease-in-out",
                  }}
                />
                <Typography>{t("Μενού")}</Typography>
              </Button>
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <CountDownSessionTimer />
              </Box>
            </>
          )}
          {renderSystemNotification}
          {authenticated &&
            (isBusiness || isNonGemiUser) &&
            renderMailboxNotification}
          {(!authenticated ||
            (authenticated && (isBusiness || isNonGemiUser))) && (
            <Box sx={{ display: { xs: "flex", md: "flex" }, ml: 1 }}>
              <Tooltip
                title={t("Εγχειρίδια χρήσης")}
                placement="bottom"
                PopperProps={{
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 3],
                      },
                    },
                  ],
                }}
              >
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={handleDocsMenuOpen}
                >
                  <MenuBookIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorElDocs}
                open={isDocsMenuOpen}
                onClose={handleDocsMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {docsMenuOptions.map((item, index) => (
                  <MenuItem
                    key={index}
                    component="a"
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDocsMenuClose}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
          <Box sx={{ display: { xs: "flex", md: "flex" } }}>
            <IconButton
              size="small"
              color="inherit"
              onClick={handleLanguageMenuOpen}
            >
              <Box
                display="flex"
                gap={"0.2rem"}
                margin="none"
                alignItems={"center"}
              >
                <LanguageOutlinedIcon />
                <Typography
                  variant="body2"
                  sx={{ display: { xs: "none", md: "flex" } }}
                >
                  {language}
                </Typography>
              </Box>
            </IconButton>
          </Box>
          {authenticated && (
            <>
              <IconButton
                sx={{
                  display: "flex",
                  borderRadius: "5px",
                  gap: 1,
                  alignItems: "center",
                }}
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <AccountCircle />
                <Typography
                  sx={{
                    display: { xs: "none", md: "flex" },
                    justifyContent: "start",
                    alignItems: "center",
                  }}
                  variant="subtitle2"
                >
                  {isCompanyPerson ? userInfo?.afm : userInfo?.username}
                </Typography>
              </IconButton>
            </>
          )}
        </Box>
      </Toolbar>
      {renderNotificationMenu}
      {renderMenu}
      {renderLanguageMenu}
      {renderNavigationMenu}
    </Box>
  );
}
